// The sequencing library of a tumour RNA analysis (roadmap R4.1).
//
// The form asks only what cannot be inferred and changes how the analysis runs:
// the library preparation (it picks the route and the expression comparison),
// the target panel of a targeted capture, the RNA input (the QC gate reads it)
// and whether the RNA shares a DNA analysis's tumour block. Strandedness is
// inferred from the reads; UMIs, RIN and DV200 are not asked (no rule reads
// them). The vocabulary mirrors libra rna_library.LIBRARY_PREPS; the backend
// validates again and is authoritative. Only the preparations the backend
// analyses are offered, and no label names a vendor or kit.

export const RNA_SOMATIC_TYPE = 'tumor-rna'
export const RNA_ANALYSIS_TYPE = 'TumorOnlyRNA'
// The build an RNA analysis aligns on: the GRCh38 RNA bundle (GENCODE v36), never a choice.
export const RNA_REFERENCE = 'GRCh38'

// "Not sure": no preparation is sent; the analysis runs as whole transcriptome
// and its expression comparison reads "library preparation not recorded".
export const NOT_SURE = 'not_sure'

export const ALL_LIBRARY_PREPS = [
  { value: 'polya', label: 'Poly(A) selection (mRNA)', supported: true },
  { value: 'ribo_depletion', label: 'Ribosomal RNA depletion (total RNA)', supported: true },
  { value: 'exome_capture', label: 'Whole-exome RNA capture', supported: true },
  { value: 'targeted_capture', label: 'Targeted capture', supported: false },
]

// The preparations offered in the form: the supported ones only.
export const LIBRARY_PREPS = ALL_LIBRARY_PREPS.filter((p) => p.supported)

export const EMPTY_LIBRARY = {
  // Preselected: the most common preparation.
  libraryPrep: 'polya',
  inputNg: '',
  // An analysis of the same tumour block whose tumour specimen this library shares.
  specimenFromAnalysisId: '',
  // The user picked the tumour block themselves (so a single linked analysis is
  // not preselected again). Never sent.
  specimenFromChosen: false,
  // The target panel (BedFile `name`, as the DNA capture kit) of a targeted RNA
  // library; blank is whole transcriptome. Never part of the library payload.
  targetPanel: '',
}

// Targeted RNA capture panels (roadmap R4.9). The analysis start accepts a panel
// only where GET rna-vocabulary says so (`capture_panels_enabled`); until then the
// panel is kept in the form but never sent, and the targeted preparation is not
// offered. When on, the preparations in `preps_needing_panel` (targeted_capture)
// are offered and need a panel, sent as the annotation's `panel`: the same key and
// value (BedFile name) the DNA kit uses. Every other preparation sends none (the
// backend refuses a panel on them).
export const TARGETED_CAPTURE = 'targeted_capture'

export const RNA_CAPTURE_OFF = Object.freeze({ enabled: false, prepsNeedingPanel: [], notYet: [] })

// The capture-panel signal of GET rna-vocabulary; off while unknown or absent.
export const rnaCaptureOf = (vocabulary) => {
  if (!vocabulary || vocabulary.capture_panels_enabled !== true) return RNA_CAPTURE_OFF
  const needing = Array.isArray(vocabulary.preps_needing_panel) ? vocabulary.preps_needing_panel : [TARGETED_CAPTURE]
  const notYet = vocabulary.library_preps_not_yet
  return {
    enabled: true,
    prepsNeedingPanel: needing.map(String),
    notYet: Array.isArray(notYet) ? notYet.map(String) : Object.keys(notYet || {}),
  }
}

// The preparations offered: the supported ones, and with the capture mode on the
// ones that take a panel (unless the backend still lists them as not yet called),
// then "Not sure".
export const libraryPrepsFor = (capture = RNA_CAPTURE_OFF) => [
  ...ALL_LIBRARY_PREPS.filter(
    (p) =>
      p.supported ||
      (capture?.enabled &&
        (capture.prepsNeedingPanel || []).includes(p.value) &&
        !(capture.notYet || []).includes(p.value)),
  ),
  { value: NOT_SURE, label: 'Not sure' },
]

// Whether this library needs a target panel (and is the only kind that sends one).
export const panelRequired = (library = EMPTY_LIBRARY, capture = RNA_CAPTURE_OFF) =>
  Boolean(capture?.enabled && (capture.prepsNeedingPanel || []).includes(library?.libraryPrep))

// The annotation `panel` of a tumour RNA analysis: '' unless the capture mode is on,
// the preparation takes a panel and one was chosen.
export const rnaPanelOf = (library = EMPTY_LIBRARY, capture = RNA_CAPTURE_OFF) =>
  panelRequired(library, capture) && !blank(library?.targetPanel) ? String(library.targetPanel) : ''

const blank = (value) => value === undefined || value === null || String(value).trim() === ''

export const isRnaSelection = (selectedFiles) => selectedFiles?.somaticAnalysisType === RNA_SOMATIC_TYPE

export const libraryPrepLabel = (value) => ALL_LIBRARY_PREPS.find((p) => p.value === value)?.label || value

export const TARGET_PANEL_REQUIRED_TEXT = 'Choose the panel this library was captured with'

// Field errors, keyed like EMPTY_LIBRARY. An empty object means the form can be sent.
export const validateLibrary = (library = EMPTY_LIBRARY, capture = RNA_CAPTURE_OFF) => {
  const errors = {}
  // A preparation that is not offered (an old draft) is simply not a choice.
  if (!libraryPrepsFor(capture).some((p) => p.value === library.libraryPrep)) {
    errors.libraryPrep = 'Choose the library preparation'
  }
  if (panelRequired(library, capture) && blank(library.targetPanel)) errors.targetPanel = TARGET_PANEL_REQUIRED_TEXT
  if (!blank(library.inputNg)) {
    const value = Number(library.inputNg)
    if (!Number.isFinite(value) || value < 0 || value > 1000000) errors.inputNg = 'Between 0 and 1000000'
  }
  return errors
}

// "Same tumour block as" names one of the analyses under "Other analyses of this
// patient". Once that analysis is removed there, the choice goes too: a stale
// choice would re-link (and share a specimen with) an analysis the user unlinked.
// Returns the same object when nothing changes, so it can run in an effect.
// With exactly one linked analysis and no choice of the user's own, the RNA is
// taken to come from that analysis's tumour block (the DNA-RNA pair). Returns the
// same object when nothing changes, so it can run in an effect.
export const preselectSpecimenSource = (library = EMPTY_LIBRARY, linkedAnalyses = []) => {
  if (!library || library.specimenFromChosen || !blank(library.specimenFromAnalysisId)) return library
  if ((linkedAnalyses || []).length !== 1) return library
  return { ...library, specimenFromAnalysisId: String(linkedAnalyses[0].analysis_id) }
}

export const pruneSpecimenSource = (library = EMPTY_LIBRARY, linkedAnalyses = []) => {
  if (!library || blank(library.specimenFromAnalysisId)) return library
  const linked = new Set((linkedAnalyses || []).map((a) => String(a.analysis_id)))
  return linked.has(String(library.specimenFromAnalysisId)) ? library : { ...library, specimenFromAnalysisId: '' }
}

// The library and specimen parts of a tumour RNA analysis request. Sharing a
// linked analysis's tumour specimen sends no specimen block of its own; a choice
// whose analysis is no longer linked is dropped and the specimen is sent.
export const buildRnaIntakeFields = ({ library = EMPTY_LIBRARY, linkedAnalyses = [], specimenPayload }) => {
  const effective = pruneSpecimenSource(library, linkedAnalyses)
  const sharesSpecimen = !blank(effective.specimenFromAnalysisId)
  return { library: buildLibraryPayload(effective), specimen: sharesSpecimen ? undefined : specimenPayload }
}

// Whether the wizard offers tumour RNA: only where the analysis start would
// accept it (GET rna-vocabulary `enabled`, answered by the same check). While
// unknown (loading, or no answer) it stays offered: the start is authoritative
// and says why it refuses.
// The reason is said in our own words: the server's text names the pipeline.
export const RNA_UNAVAILABLE_TEXT =
  'Tumour RNA analyses cannot be started here. Uploaded RNA results from other labs can still be viewed.'

export const rnaAvailability = (vocabulary) =>
  vocabulary && vocabulary.enabled === false
    ? { enabled: false, reason: RNA_UNAVAILABLE_TEXT }
    : { enabled: true, reason: null }

// The `annotation.library` payload of a tumour RNA analysis.
export const buildLibraryPayload = (library = EMPTY_LIBRARY) => {
  const out = { analyte: 'rna' }
  if (!blank(library.libraryPrep) && library.libraryPrep !== NOT_SURE) out.libraryPrep = library.libraryPrep
  if (!blank(library.inputNg)) out.inputNg = Number(library.inputNg)
  if (!blank(library.specimenFromAnalysisId)) out.specimenFromAnalysisId = Number(library.specimenFromAnalysisId)
  return out
}

// One line for the review step: what was recorded, "not recorded" for the rest.
export const describeLibrary = (library = EMPTY_LIBRARY) => {
  const prep = library.libraryPrep
  const parts = [blank(prep) || prep === NOT_SURE ? 'preparation not recorded' : libraryPrepLabel(prep)]
  if (prep === TARGETED_CAPTURE && !blank(library.targetPanel)) parts.push(`panel ${library.targetPanel}`)
  if (!blank(library.inputNg)) parts.push(`${Number(library.inputNg)} ng input`)
  return parts.join(', ')
}
